import type { PlacementFormState, PlacementFormErrors, AtsSourceValues } from "../types"
import { AtsHint, AtsRateHint } from "../fields/display-helpers"
import { DateField } from "../fields/date-field"
import { NumericField } from "../fields/numeric-field"
import type { CurrencyItem } from "../fields/numeric-field"
import { FIELD_ANCHOR_ID_PREFIX } from "../constants"
import { useTranslation } from "@/hooks/use-translation"

export interface DatesRatesSectionProps {
  form: PlacementFormState
  errors: PlacementFormErrors
  showError: (field: keyof PlacementFormErrors) => boolean | undefined
  atsSource: AtsSourceValues
  currencyItems?: CurrencyItem[]
  diffs?: {
    startDateChanged?: boolean
    endDateChanged?: boolean
    billRateChanged?: boolean
    payRateChanged?: boolean
    billCurrencyChanged?: boolean
    payCurrencyChanged?: boolean
  }
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  onBillRateChange: (rate: string) => void
  onPayRateChange: (rate: string) => void
  onBillCurrencyChange: (code: string | null) => void
  onPayCurrencyChange: (code: string | null) => void
  disabled?: boolean
  payRateCurrencyWarning?: string
}

export function DatesRatesSection({
  form,
  errors,
  showError,
  atsSource: ats,
  currencyItems,
  diffs,
  onStartDateChange,
  onEndDateChange,
  onBillRateChange,
  onPayRateChange,
  onBillCurrencyChange,
  onPayCurrencyChange,
  disabled,
  payRateCurrencyWarning,
}: DatesRatesSectionProps) {
  const { t } = useTranslation()
  const { startDateChanged, endDateChanged, billRateChanged, payRateChanged, billCurrencyChanged, payCurrencyChanged } = diffs ?? {}

  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">
        {t("sectionDatesRates")}
      </h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div id={`${FIELD_ANCHOR_ID_PREFIX}startDate`}>
          <DateField
            label={t("labelStartDate")}
            required
            value={form.startDate}
            onChange={onStartDateChange}
            atsHint={<AtsHint value={ats.startDate} />}
            changedPill={startDateChanged}
            error={showError("startDate") ? errors.startDate : undefined}
            disabled={disabled}
          />
        </div>
        <div id={`${FIELD_ANCHOR_ID_PREFIX}endDate`}>
          <DateField
            label={t("labelEndDate")}
            required
            value={form.endDate}
            onChange={onEndDateChange}
            atsHint={<AtsHint value={ats.endDate} />}
            changedPill={endDateChanged}
            error={showError("endDate") ? errors.endDate : undefined}
            disabled={disabled}
          />
        </div>
        {/* Bill / Pay rate wrappers cover both the amount input AND the embedded currency
            dropdown. The hook redirects billCurrencyCode/payCurrencyCode missing-field clicks
            to these same anchors so the user lands here either way. */}
        <div id={`${FIELD_ANCHOR_ID_PREFIX}billRate`}>
          <NumericField
            label={t("labelBillRate")}
            required
            value={form.billRate}
            onChange={onBillRateChange}
            atsHint={<AtsRateHint value={ats.billRate} currencyCode={ats.billCurrencyCode} />}
            changedPill={billRateChanged || billCurrencyChanged}
            currencyCode={form.billCurrencyCode}
            onCurrencyChange={onBillCurrencyChange}
            currencyItems={currencyItems}
            error={showError("billRate") ? errors.billRate : undefined}
            currencyError={showError("billCurrencyCode") ? errors.billCurrencyCode : undefined}
            disabled={disabled}
          />
        </div>
        <div id={`${FIELD_ANCHOR_ID_PREFIX}payRate`}>
          <NumericField
            label={t("labelPayRate")}
            required
            value={form.payRate}
            onChange={onPayRateChange}
            atsHint={<AtsRateHint value={ats.payRate} currencyCode={ats.payCurrencyCode} />}
            changedPill={payRateChanged || payCurrencyChanged}
            currencyCode={form.payCurrencyCode}
            onCurrencyChange={onPayCurrencyChange}
            currencyItems={currencyItems}
            error={showError("payRate") ? errors.payRate : undefined}
            currencyError={showError("payCurrencyCode") ? errors.payCurrencyCode : undefined}
            warning={payRateCurrencyWarning}
            disabled={disabled}
          />
        </div>
      </div>
    </section>
  )
}
